import { useState, useEffect } from 'react'
import { AdminApiService } from '../services/api'
import { DashboardStats, UTMAnalytics, UserAnalytics, SubscriptionTier, DesignVariantStats } from '../types/admin'
import CassetteLoader from '../components/CassetteLoader'
import { 
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

export function Analytics() {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null)
  const [utmAnalytics, setUtmAnalytics] = useState<UTMAnalytics[]>([])
  const [designVariants, setDesignVariants] = useState<DesignVariantStats[]>([])
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [statsData, utmData, userData] = await Promise.all([
        AdminApiService.getDashboardStats(),
        AdminApiService.getUTMAnalytics(),
        AdminApiService.getUserAnalytics()
      ])
      
      setDashboardStats(statsData)
      setUtmAnalytics(utmData?.utmSources ?? [])
      setDesignVariants(utmData?.designVariants ?? [])
      setUserAnalytics(userData)
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics')
      toast.error('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const getGrowthIndicator = (current: number, previous: number) => {
    if (previous === 0) return { value: 100, isPositive: true }
    const growth = ((current - previous) / previous) * 100
    return { value: Math.abs(growth), isPositive: growth >= 0 }
  }

  const StatCard = ({ 
    title, 
    value, 
    previousValue, 
    icon: Icon, 
    format = 'number',
    subtitle 
  }: {
    title: string
    value: number
    previousValue?: number
    icon: any
    format?: 'number' | 'currency' | 'percentage'
    subtitle?: string
  }) => {
    const growth = previousValue ? getGrowthIndicator(value, previousValue) : null
    const formattedValue = format === 'currency' ? formatCurrency(value) : 
                          format === 'percentage' ? `${value}%` : formatNumber(value)

    return (
      <div className="card">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className="h-8 w-8 text-primary-600" />
          </div>
          <div className="ml-4 flex-1">
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-gray-900">{formattedValue}</p>
              {growth && (
                <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                  growth.isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  {growth.isPositive ? (
                    <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                  ) : (
                    <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                  )}
                  <span className="ml-1">{growth.value.toFixed(1)}%</span>
                </div>
              )}
            </div>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CassetteLoader size={80} text="Loading analytics..." />
      </div>
    )
  }

  if (error || !dashboardStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load analytics data</p>
          <button onClick={loadAnalytics} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const totalUsers =
    dashboardStats.userStats?.totalUsers ?? dashboardStats.users?.totalUsers ?? 0
  const activeUsers =
    dashboardStats.userStats?.activeUsers ?? dashboardStats.users?.activeUsers ?? 0
  const premiumUsers =
    dashboardStats.userStats?.premiumUsers ?? dashboardStats.users?.premiumUsers ?? 0

  const subscriptionStats =
    dashboardStats.subscriptionStats ??
    (dashboardStats.subscriptions
      ? ([
          { tier: SubscriptionTier.STARTER, count: dashboardStats.subscriptions.starterUsers, percentage: 0 },
          { tier: SubscriptionTier.ARTIST, count: dashboardStats.subscriptions.artistUsers, percentage: 0 },
          { tier: SubscriptionTier.STUDIO, count: dashboardStats.subscriptions.studioUsers, percentage: 0 },
        ].map((s) => ({
          ...s,
          percentage: totalUsers > 0 ? (s.count / totalUsers) * 100 : 0,
        })) as DashboardStats['subscriptionStats'])
      : [])

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Overview of user growth, subscriptions, and performance metrics
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={totalUsers}
          previousValue={dashboardStats.userStats?.previousPeriodUsers}
          icon={UserGroupIcon}
          subtitle="All registered users"
        />
        
        <StatCard
          title="Active Users"
          value={activeUsers}
          previousValue={dashboardStats.userStats?.previousPeriodActiveUsers}
          icon={ArrowTrendingUpIcon}
          subtitle="Users active in last 30 days"
        />
        
        <StatCard
          title="Premium Users"
          value={premiumUsers}
          previousValue={dashboardStats.userStats?.previousPeriodPremiumUsers}
          icon={ChartBarIcon}
          subtitle="Artist + Studio subscribers"
        />
        
        <StatCard
          title="Monthly Revenue"
          value={dashboardStats.revenueStats?.monthlyRevenue || 0}
          previousValue={dashboardStats.revenueStats?.previousMonthRevenue}
          icon={CurrencyDollarIcon}
          format="currency"
          subtitle="Recurring monthly revenue"
        />
      </div>

      {/* Subscription Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Distribution</h3>
          <div className="space-y-4">
            {(subscriptionStats || []).map((stat, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`h-4 w-4 rounded-full mr-3 ${
                    stat.tier === SubscriptionTier.STARTER ? 'bg-gray-400' :
                    stat.tier === SubscriptionTier.ARTIST ? 'bg-blue-400' : 'bg-green-400'
                  }`} />
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {stat.tier} Tier
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-900">
                    {formatNumber(stat.count)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {stat.percentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Rates */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Conversion Rates</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Free to Artist</span>
              <span className="text-sm font-bold text-gray-900">
                {(dashboardStats?.conversionRates?.freeToArtist || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Artist to Studio</span>
              <span className="text-sm font-bold text-gray-900">
                {(dashboardStats?.conversionRates?.artistToStudio || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Overall Conversion</span>
              <span className="text-sm font-bold text-gray-900">
                {(dashboardStats?.conversionRates?.overallConversion || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* UTM Performance */}
      {utmAnalytics.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">UTM Source Performance</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>UTM Source</th>
                  <th>Users</th>
                  <th>Conversions</th>
                  <th>Conversion Rate</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {utmAnalytics.map((utm, index) => (
                  <tr key={index}>
                    <td className="font-medium">
                      {utm.source || 'Direct'}
                    </td>
                    <td>{formatNumber(utm.totalUsers)}</td>
                    <td>{formatNumber(utm.conversions ?? (utm as any).premiumUsers ?? 0)}</td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        utm.conversionRate > 10 ? 'bg-green-100 text-green-800' :
                        utm.conversionRate > 5 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {utm.conversionRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="font-medium">{formatCurrency(utm.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Design A/B variant performance */}
      {designVariants.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Design Variant Performance</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Design</th>
                  <th>Signups</th>
                  <th>Drive Connected</th>
                  <th>Drive Rate</th>
                  <th>Premium</th>
                  <th>Premium Rate</th>
                </tr>
              </thead>
              <tbody>
                {designVariants.map((variant) => (
                  <tr key={variant.design}>
                    <td className="font-medium capitalize">{variant.design}</td>
                    <td>{formatNumber(variant.totalUsers)}</td>
                    <td>{formatNumber(variant.driveConnected)}</td>
                    <td>{variant.driveConnectRate.toFixed(1)}%</td>
                    <td>{formatNumber(variant.premiumUsers)}</td>
                    <td>{variant.premiumConvertRate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Growth Chart Placeholder */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Growth Over Time</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Chart visualization coming soon</p>
            <p className="text-sm text-gray-400">
              Total users: {formatNumber(totalUsers)}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">This Week</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New Users</span>
              <span className="text-sm font-medium">{formatNumber(userAnalytics?.newUsersThisWeek || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Upgrades</span>
              <span className="text-sm font-medium">{formatNumber(userAnalytics?.upgradesThisWeek || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Revenue</span>
              <span className="text-sm font-medium">{formatCurrency(userAnalytics?.revenueThisWeek || 0)}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">This Month</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New Users</span>
              <span className="text-sm font-medium">{formatNumber(userAnalytics?.newUsersThisMonth || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Upgrades</span>
              <span className="text-sm font-medium">{formatNumber(userAnalytics?.upgradesThisMonth || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Revenue</span>
              <span className="text-sm font-medium">{formatCurrency(userAnalytics?.revenueThisMonth || 0)}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Performing</h3>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="text-gray-600">UTM Source: </span>
              <span className="font-medium">
                {utmAnalytics[0]?.source || 'Direct'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-gray-600">Best Converting: </span>
              <span className="font-medium">
                {utmAnalytics[0]?.conversionRate.toFixed(1) || '0.0'}%
              </span>
            </div>
            <div className="text-sm">
              <span className="text-gray-600">Highest Revenue: </span>
              <span className="font-medium">
                {formatCurrency(utmAnalytics[0]?.revenue || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}